/** Shared Excel → row parsing for bulk reserved imports (assign tickets / assign package). */

import parsePhoneNumber from 'libphonenumber-js/min'

export const MAX_BULK_RESERVED_ROWS = 2000

const DEFAULT_BULK_PHONE_COUNTRY =
  (typeof process !== 'undefined' &&
    process.env &&
    process.env.REACT_APP_DEFAULT_PHONE_COUNTRY) ||
  'US'

export function normalizeHeaderKey(raw) {
  return String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
}

export function headerToCanonical(normKey) {
  const synonyms = {
    organization: 'organization',
    org: 'organization',
    email: 'email',
    first_name: 'first_name',
    firstname: 'first_name',
    last_name: 'last_name',
    lastname: 'last_name',
    phone_number: 'phone_number',
    phone_numbers: 'phone_number',
    phone: 'phone_number',
    buyer_type: 'buyer_type',
    buyertype: 'buyer_type',
    buyer_type_code: 'buyer_type_code',
    code: 'buyer_type_code',
    price: 'price',
    price_tier: 'price_tier',
    pricetier: 'price_tier',
    pricing_tier: 'price_tier',
    price_per: 'price_per',
    priceper: 'price_per',
    price_per_seat: 'price_per',
    priceperseat: 'price_per',
    custom_fee: 'custom_fee',
    customfee: 'custom_fee',
    custom_fee_name: 'custom_fee',
    section: 'section',
    section_name: 'section',
    row: 'row',
    row_name: 'row',
    seats: 'seats',
    email_addr: 'email',
    ga: 'ga',
    quantity: 'quantity',
  }
  return synonyms[normKey] || normKey
}

function parseTruthyGaCell(raw) {
  if (raw === true || raw === 1) return true
  const s = String(raw ?? '')
    .trim()
    .toLowerCase()
  return s === 'true' || s === 'yes' || s === 'y' || s === '1'
}

/** Row cell is GA designation (GA, GA5, GA 5), not reserved seating like G16. */
export function rowLabelIndicatesGa(rowLabel) {
  const s = String(rowLabel ?? '').trim()
  if (!s) return false
  return /^ga(\s*\d*)?$/i.test(s)
}

/** Section labels like GA, GA1, GA 5, or General Admission — not STAGE/MEGA. */
export function sectionLabelIndicatesGa(section) {
  const s = String(section ?? '')
    .trim()
    .toLowerCase()
  if (!s) return false
  if (s.includes('general admission')) return true
  return /(?:^|[^a-z])ga(?:\d+|\b)/i.test(s)
}

/** Org UUID -> section labels treated as GA during bulk reserved import. */
const ORGANIZATION_BULK_GA_SECTIONS = {
  'dbeea528-852e-43d0-b607-c8201d7a5f1c': new Set([
    'b',
    'c',
    'k',
    'l',
    'o',
    'p',
    'x',
    'y',
    'z',
  ]),
}

export function isOrganizationGaSection(organizationUUID, section) {
  const orgKey = String(organizationUUID ?? '')
    .trim()
    .toLowerCase()
  const sections = ORGANIZATION_BULK_GA_SECTIONS[orgKey]
  if (!sections) return false
  return sections.has(
    String(section ?? '')
      .trim()
      .toLowerCase()
  )
}

function isBulkReservedGaAssignment(assignment, organizationUUID, row) {
  if (parseTruthyGaCell(assignment?.ga)) return true
  if (parseTruthyGaCell(row?.ga)) return true
  if (rowLabelIndicatesGa(assignment?.row)) return true
  if (rowLabelIndicatesGa(row?.row)) return true
  if (sectionLabelIndicatesGa(assignment?.section)) return true
  if (sectionLabelIndicatesGa(row?.section)) return true
  return isOrganizationGaSection(organizationUUID, assignment?.section)
}

export const REQUIRED_CANONICAL = [
  'email',
  'first_name',
  'last_name',
  'section',
  'row',
  'seats',
]

export function buildHeaderMap(headerRow) {
  const map = {}
  if (!Array.isArray(headerRow)) return map
  const allowed = new Set([
    ...REQUIRED_CANONICAL,
    'organization',
    'phone_number',
    'buyer_type',
    'buyer_type_code',
    'price',
    'price_tier',
    'price_per',
    'custom_fee',
    'ga',
    'quantity',
  ])
  headerRow.forEach((cell, colIndex) => {
    const canon = headerToCanonical(normalizeHeaderKey(cell))
    if (allowed.has(canon)) {
      map[colIndex] = canon
    }
  })
  return map
}

export function rowArrayToObject(headerMap, rowArray, excelRow) {
  const obj = { excelRow }
  Object.entries(headerMap).forEach(([colStr, field]) => {
    const col = Number(colStr)
    obj[field] = rowArray[col] != null ? rowArray[col] : ''
  })
  return obj
}

const NUMERIC_SEAT_RANGE = /^(\d+)\s*[-–—]\s*(\d+)$/

export function expandSeatRange(seatStartRaw, seatEndRaw) {
  const startStr = String(seatStartRaw ?? '').trim()
  const endStr = String(seatEndRaw ?? '').trim()
  if (!startStr) return []
  if (!endStr || endStr === startStr) return [startStr]

  const startNum = Number(startStr)
  const endNum = Number(endStr)
  if (
    Number.isFinite(startNum) &&
    Number.isFinite(endNum) &&
    Number.isInteger(startNum) &&
    Number.isInteger(endNum)
  ) {
    const lo = Math.min(startNum, endNum)
    const hi = Math.max(startNum, endNum)
    const seats = []
    for (let i = lo; i <= hi; i += 1) {
      seats.push(String(i))
    }
    return seats
  }

  return [startStr]
}

function expandSeatToken(token) {
  if (!token) return []
  const match = token.match(NUMERIC_SEAT_RANGE)
  if (!match) return [token]
  return expandSeatRange(match[1], match[2])
}

/** Comma/semicolon-separated seats, with numeric ranges expanded (1-3 → 1,2,3). */
export function parseSeatTokens(seatsRaw) {
  return String(seatsRaw ?? '')
    .split(/[,;]/)
    .flatMap((s) => expandSeatToken(s.trim()))
    .filter(Boolean)
}

function expandSeatsCell(seatsRaw) {
  const tokens = parseSeatTokens(seatsRaw)
  if (tokens.length) return tokens.join(',')
  return seatsRaw
}

function splitDelimitedCell(raw, delimiter) {
  return String(raw ?? '')
    .split(delimiter)
    .map((s) => s.trim())
    .filter(Boolean)
}

function mapSeatAssignment(assignment) {
  return {
    section: assignment.section,
    row: assignment.row,
    seats: expandSeatsCell(assignment.seats),
  }
}

export function getBulkRowSeatAssignments(row) {
  if (Array.isArray(row?.seat_assignments) && row.seat_assignments.length) {
    return row.seat_assignments.map(mapSeatAssignment)
  }

  const rowParts = splitDelimitedCell(row.row, ',')
  const seatBlocks = splitDelimitedCell(row.seats, ';')
  if (rowParts.length > 1 && seatBlocks.length === rowParts.length) {
    return rowParts.map((rowLabel, i) =>
      mapSeatAssignment({
        section: row.section,
        row: rowLabel,
        seats: seatBlocks[i],
      })
    )
  }

  return [
    {
      section: row.section,
      row: row.row,
      seats: expandSeatsCell(row.seats),
    },
  ]
}

function mergeSeatAssignments(assignments) {
  const byKey = new Map()

  for (const assignment of assignments) {
    const section = String(assignment.section ?? '').trim()
    const rowLabel = String(assignment.row ?? '').trim()
    const key = `${section.toLowerCase()}|${rowLabel.toLowerCase()}`
    const seats = parseSeatTokens(assignment.seats)

    if (!byKey.has(key)) {
      byKey.set(key, { section, row: rowLabel, seats: [...seats] })
      continue
    }

    byKey.get(key).seats.push(...seats)
  }

  return [...byKey.values()].map((assignment) => ({
    section: assignment.section,
    row: assignment.row,
    seats: uniqueSortedSeats(assignment.seats).join(','),
  }))
}

function preferNonEmpty(existing, incoming) {
  const current = String(existing ?? '').trim()
  if (current) return existing
  const next = String(incoming ?? '').trim()
  return next || existing
}

export function normalizePricingTierLabel(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
}

function stripPricingTierParenthetical(value) {
  return normalizePricingTierLabel(value)
    .replace(/\([^)]*\)/g, '')
    .trim()
}

function extractPricingTierParentheticalCode(value) {
  const match = String(value ?? '').match(/\(([A-Za-z0-9]+)\)\s*$/)
  return match ? match[1].trim().toLowerCase() : ''
}

function simplifyPricingTierLabel(value) {
  return normalizePricingTierLabel(value)
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function pricingTierNameTokens(value) {
  return normalizePricingTierLabel(value)
    .split(/[,;/|]+/)
    .map((s) => s.trim())
    .filter(Boolean)
}

/** Spreadsheet amounts arrive formatted ("$1,600.00", "(208.25)"), not as raw numbers. */
export function parseBulkImportAmount(raw) {
  if (raw == null) return undefined
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : undefined

  let s = String(raw).trim()
  if (!s) return undefined

  const parenthesizedNegative = /^\(.*\)$/.test(s)
  if (parenthesizedNegative) s = s.slice(1, -1)

  s = s.replace(/[^0-9.-]/g, '')
  if (!/\d/.test(s)) return undefined

  const amount = Number(s)
  if (!Number.isFinite(amount)) return undefined

  return parenthesizedNegative ? -amount : amount
}

function resolveBulkPricingTierPriceHint(row) {
  const fromPricePer = parseBulkImportAmount(row?.price_per)
  if (fromPricePer != null) return fromPricePer

  const fromPricePerCamel = parseBulkImportAmount(row?.pricePer)
  if (fromPricePerCamel != null) return fromPricePerCamel

  return parseBulkImportAmount(row?.pricingTier?.price)
}

export function normalizePackagePricingTiersList(pricingTiers) {
  if (Array.isArray(pricingTiers)) return pricingTiers
  if (pricingTiers && typeof pricingTiers === 'object') {
    return Object.values(pricingTiers)
  }
  return []
}

function normalizePackagePricingTierEntity(tier) {
  const attrs = tier?.attributes ?? tier
  const id = tier?.id ?? attrs?.id
  const normalizedId = Number(id)
  return {
    ...(Number.isFinite(normalizedId) ? { id: normalizedId } : {}),
    name: String(attrs?.name ?? tier?.name ?? '').trim(),
    price: Number(attrs?.price ?? tier?.price),
  }
}

function toMatchedPricingTier(tier) {
  if (!tier) return null
  return {
    ...(tier.id != null ? { id: tier.id } : {}),
    name: tier.name,
    ...(Number.isFinite(tier.price) ? { price: tier.price } : {}),
  }
}

/** Match export PRICE TIER text to a package pricing tier ({ id, name, price }). */
export function matchPackagePricingTierLabel(
  label,
  packagePricingTiers,
  priceHint
) {
  const tiersList = normalizePackagePricingTiersList(packagePricingTiers)
  if (!tiersList.length) {
    return null
  }

  const normalizedLabel = normalizePricingTierLabel(label)
  const strippedLabel = stripPricingTierParenthetical(label)
  const simplifiedLabel = simplifyPricingTierLabel(label)
  const labelCode = extractPricingTierParentheticalCode(label)
  const bareLabel = normalizedLabel.replace(/[()]/g, '').trim()
  const numericHint = Number(priceHint)

  const tiers = tiersList
    .map(normalizePackagePricingTierEntity)
    .filter((tier) => tier.name || Number.isFinite(tier.price))

  if (normalizedLabel) {
    const exact = tiers.find(
      (tier) => normalizePricingTierLabel(tier.name) === normalizedLabel
    )
    if (exact) {
      return toMatchedPricingTier(exact)
    }

    if (strippedLabel) {
      const strippedExact = tiers.find(
        (tier) => stripPricingTierParenthetical(tier.name) === strippedLabel
      )
      if (strippedExact) {
        return toMatchedPricingTier(strippedExact)
      }
    }

    if (simplifiedLabel) {
      const simplifiedExact = tiers.find(
        (tier) => simplifyPricingTierLabel(tier.name) === simplifiedLabel
      )
      if (simplifiedExact) {
        return toMatchedPricingTier(simplifiedExact)
      }
    }

    if (labelCode) {
      const codeMatches = tiers.filter(
        (tier) => extractPricingTierParentheticalCode(tier.name) === labelCode
      )
      if (codeMatches.length === 1) {
        return toMatchedPricingTier(codeMatches[0])
      }
    }

    if (bareLabel && bareLabel.length <= 3) {
      const shortCodeMatches = tiers.filter(
        (tier) => extractPricingTierParentheticalCode(tier.name) === bareLabel
      )
      if (shortCodeMatches.length === 1) {
        return toMatchedPricingTier(shortCodeMatches[0])
      }
    }

    const labelTokens = pricingTierNameTokens(label)
    if (labelTokens.length) {
      const tokenMatches = tiers.filter((tier) => {
        const tokens = pricingTierNameTokens(tier.name)
        return labelTokens.some((token) => tokens.includes(token))
      })
      if (tokenMatches.length === 1) {
        return toMatchedPricingTier(tokenMatches[0])
      }
    }

    if (sectionLabelIndicatesGa(label)) {
      const gaMatches = tiers.filter((tier) =>
        sectionLabelIndicatesGa(tier.name)
      )
      if (gaMatches.length === 1) {
        return toMatchedPricingTier(gaMatches[0])
      }
    }

    const fuzzy = tiers.find((tier) => {
      const tierName = normalizePricingTierLabel(tier.name)
      const strippedTierName = stripPricingTierParenthetical(tier.name)
      const simplifiedTierName = simplifyPricingTierLabel(tier.name)

      if (tierName.length >= 3 && normalizedLabel.includes(tierName)) {
        return true
      }
      if (
        strippedTierName.length >= 3 &&
        strippedLabel &&
        strippedLabel.includes(strippedTierName)
      ) {
        return true
      }
      if (
        strippedTierName.length >= 3 &&
        strippedLabel &&
        strippedTierName.includes(strippedLabel)
      ) {
        return true
      }
      if (
        simplifiedTierName.length >= 3 &&
        simplifiedLabel &&
        simplifiedLabel.includes(simplifiedTierName)
      ) {
        return true
      }
      return false
    })
    if (fuzzy) {
      return toMatchedPricingTier(fuzzy)
    }
  }

  if (Number.isFinite(numericHint)) {
    const priceMatches = tiers.filter((tier) => tier.price === numericHint)
    if (priceMatches.length === 1) {
      return toMatchedPricingTier(priceMatches[0])
    }
    if (priceMatches.length > 1) {
      const disambiguated = priceMatches.find((tier) => {
        const strippedTierName = stripPricingTierParenthetical(tier.name)
        const simplifiedTierName = simplifyPricingTierLabel(tier.name)
        return (
          (strippedLabel && strippedTierName === strippedLabel) ||
          (simplifiedLabel && simplifiedTierName === simplifiedLabel) ||
          (labelCode &&
            extractPricingTierParentheticalCode(tier.name) === labelCode)
        )
      })
      if (disambiguated) {
        return toMatchedPricingTier(disambiguated)
      }
    }
  }

  return null
}

export function attachPackagePricingTier(row, packagePricingTiers) {
  const tiersList = normalizePackagePricingTiersList(packagePricingTiers)
  const label = String(
    row.price_tier ?? row.pricing_tier ?? row.priceTier ?? row.section ?? ''
  ).trim()
  const priceHint = resolveBulkPricingTierPriceHint(row)
  if ((!label && !Number.isFinite(priceHint)) || !tiersList.length) return row

  const matched = matchPackagePricingTierLabel(label, tiersList, priceHint)
  if (!matched) {
    return label ? { ...row, price_tier_unmatched: label } : row
  }

  return {
    ...row,
    price_tier: label || matched.name,
    pricingTier: matched,
  }
}

function normalizeBuyerTypeEntity(entity) {
  const attrs = entity?.attributes ?? entity
  return {
    id: entity?.id ?? attrs?.id,
    name: String(attrs?.name ?? '').trim(),
    abbreviation: String(attrs?.abbreviation ?? '').trim(),
    complimentary: Boolean(attrs?.complimentary),
  }
}

export function normalizeBuyerTypeLabel(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
}

function simplifyBuyerTypeLabel(value) {
  return normalizeBuyerTypeLabel(value)
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function stripSeasonBuyerTypePrefix(label) {
  return normalizeBuyerTypeLabel(label)
    .replace(/^season\s+/, '')
    .trim()
}

export function matchOrganizationBuyerTypeLabel(label, buyerTypeEntities) {
  if (!Array.isArray(buyerTypeEntities) || !buyerTypeEntities.length) {
    return null
  }

  const normalizedLabel = normalizeBuyerTypeLabel(label)
  if (!normalizedLabel) return null

  const buyerTypes = buyerTypeEntities
    .map(normalizeBuyerTypeEntity)
    .filter((bt) => bt.id != null && (bt.name || bt.abbreviation))

  const candidates = [
    normalizedLabel,
    stripSeasonBuyerTypePrefix(normalizedLabel),
    simplifyBuyerTypeLabel(label),
    simplifyBuyerTypeLabel(stripSeasonBuyerTypePrefix(normalizedLabel)),
  ].filter(Boolean)

  const looksComp = candidates.some(
    (candidate) =>
      candidate.includes('comp') || candidate.includes('complimentary')
  )

  const tryMatch = (predicate) => buyerTypes.find(predicate)

  for (const candidate of candidates) {
    const exact = tryMatch((bt) => {
      const name = simplifyBuyerTypeLabel(bt.name)
      const abbr = simplifyBuyerTypeLabel(bt.abbreviation)
      return candidate === name || candidate === abbr
    })
    if (exact) return exact

    const fuzzy = tryMatch((bt) => {
      const name = simplifyBuyerTypeLabel(bt.name)
      const abbr = simplifyBuyerTypeLabel(bt.abbreviation)
      return (
        (name && (candidate.includes(name) || name.includes(candidate))) ||
        (abbr && (candidate.includes(abbr) || abbr.includes(candidate)))
      )
    })
    if (fuzzy) return fuzzy
  }

  if (looksComp) {
    const compMatches = buyerTypes.filter((bt) => bt.complimentary)
    if (compMatches.length === 1) return compMatches[0]
  }

  return null
}

export function attachOrganizationBuyerType(row, buyerTypeEntities) {
  const label = String(
    row.buyer_type_code ?? row.buyer_type ?? row.code ?? ''
  ).trim()
  if (!label || !buyerTypeEntities?.length) return row

  const matched = matchOrganizationBuyerTypeLabel(label, buyerTypeEntities)
  if (!matched) {
    return { ...row, buyer_type_code: label, buyer_type_code_unmatched: label }
  }

  return {
    ...row,
    buyer_type_code: label,
    selectedBuyerType: matched.id,
    buyerTypeName: matched.name,
  }
}

function normalizeCustomFeeEntity(fee) {
  const attrs = fee?.attributes ?? fee
  const id = fee?.id ?? attrs?.id
  const normalizedId = Number(id)
  return {
    ...(Number.isFinite(normalizedId) ? { id: normalizedId } : {}),
    name: String(attrs?.name ?? fee?.name ?? '').trim(),
    option: String(attrs?.option ?? fee?.option ?? 'amount').trim(),
    feeAmount: Number(attrs?.feeAmount ?? fee?.fee_amount ?? fee?.feeAmount),
  }
}

function normalizeCustomFeeLabel(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
}

export function matchCustomFeeByName(label, customFees) {
  if (!Array.isArray(customFees) || !customFees.length) return null

  const normalizedLabel = normalizeCustomFeeLabel(label)
  if (!normalizedLabel) return null

  const fees = customFees
    .map(normalizeCustomFeeEntity)
    .filter((fee) => fee.id != null && fee.name)

  const exact = fees.find(
    (fee) => normalizeCustomFeeLabel(fee.name) === normalizedLabel
  )
  if (exact) return exact

  return (
    fees.find((fee) => {
      const feeName = normalizeCustomFeeLabel(fee.name)
      return (
        feeName.includes(normalizedLabel) || normalizedLabel.includes(feeName)
      )
    }) || null
  )
}

function resolveCustomFeeLineAmount(feeEntity, percentBase) {
  if (!feeEntity) return 0
  const fee = normalizeCustomFeeEntity(feeEntity)
  const raw = Number(fee.feeAmount)
  if (!Number.isFinite(raw)) return 0
  if (fee.option === 'percent') {
    const base = Number(percentBase) || 0
    return (base * raw) / 100
  }
  return raw
}

function countBulkImportTickets(row) {
  if (Array.isArray(row?.seat_assignments) && row.seat_assignments.length) {
    return row.seat_assignments.reduce(
      (sum, assignment) => sum + parseSeatTokens(assignment.seats).length,
      0
    )
  }
  const qty = Number(row.quantity)
  if (Number.isFinite(qty) && qty > 0) return Math.floor(qty)
  return parseSeatTokens(row.seats).length
}

function resolveBulkCustomFeePercentBase(row, pricingTier) {
  const rowPrice = Number(row.price)
  if (Number.isFinite(rowPrice) && rowPrice > 0) return rowPrice

  const count = countBulkImportTickets(row)
  const tierPrice = Number(pricingTier?.price)
  if (Number.isFinite(tierPrice) && tierPrice > 0 && count > 0) {
    return tierPrice * count
  }
  return 0
}

export function attachCustomFee(row, customFeeEntities) {
  const label = String(
    row.custom_fee ?? row.customFee ?? row.custom_fee_name ?? ''
  ).trim()
  if (!label || !customFeeEntities?.length) return row

  const matched = matchCustomFeeByName(label, customFeeEntities)
  if (!matched) {
    return { ...row, custom_fee: label, custom_fee_unmatched: label }
  }

  const totalFeeAmount =
    Math.round(
      resolveCustomFeeLineAmount(
        matched,
        resolveBulkCustomFeePercentBase(row, row.pricingTier)
      ) * 100
    ) / 100

  return {
    ...row,
    custom_fee: label,
    customFeeIds: [matched.id],
    customFeeName: matched.name,
    totalFeeAmount,
  }
}

export function attachPackageImportMappings(
  row,
  { packagePricingTiers = [], buyerTypes = [], customFees = [] } = {}
) {
  const tiersList = normalizePackagePricingTiersList(packagePricingTiers)
  let next = row
  if (tiersList.length) {
    next = attachPackagePricingTier(next, tiersList)
  }
  if (buyerTypes.length) {
    next = attachOrganizationBuyerType(next, buyerTypes)
  }
  if (customFees.length) {
    next = attachCustomFee(next, customFees)
  }
  return next
}

/** One import row (reserved order) per email; merges all seat blocks for that email. */
export function consolidateBulkUploadRowsByEmail(rows) {
  const byEmail = new Map()

  for (const row of rows) {
    const emailKey = String(row.email ?? '')
      .trim()
      .toLowerCase()
    if (!emailKey) continue

    const assignments = getBulkRowSeatAssignments(row)

    if (!byEmail.has(emailKey)) {
      byEmail.set(emailKey, {
        ...row,
        email: String(row.email ?? '').trim(),
        excelRow: row.excelRow,
        excelRows: [row.excelRow],
        seat_assignments: [...assignments],
      })
      continue
    }

    const existing = byEmail.get(emailKey)
    existing.excelRows.push(row.excelRow)
    existing.seat_assignments.push(...assignments)
    existing.first_name = preferNonEmpty(existing.first_name, row.first_name)
    existing.last_name = preferNonEmpty(existing.last_name, row.last_name)
    existing.phone_number = preferNonEmpty(
      existing.phone_number,
      row.phone_number
    )
    existing.organization = preferNonEmpty(
      existing.organization,
      row.organization
    )
    existing.buyer_type_code = preferNonEmpty(
      existing.buyer_type_code,
      row.buyer_type_code ?? row.buyer_type
    )
    existing.price_tier = preferNonEmpty(existing.price_tier, row.price_tier)
    existing.price_per = preferNonEmpty(existing.price_per, row.price_per)
    existing.custom_fee = preferNonEmpty(existing.custom_fee, row.custom_fee)

    const existingPrice = parseBulkImportAmount(existing.price)
    const rowPrice = parseBulkImportAmount(row.price)
    if (existingPrice != null && rowPrice != null) {
      existing.price = existingPrice + rowPrice
    } else if (
      (existing.price == null || String(existing.price).trim() === '') &&
      row.price != null &&
      String(row.price).trim() !== ''
    ) {
      existing.price = row.price
    }
  }

  return [...byEmail.values()].map((row) => {
    const seatAssignments = mergeSeatAssignments(row.seat_assignments)
    const consolidated = {
      ...row,
      seat_assignments: seatAssignments,
      excelRow: Math.min(...row.excelRows.map((n) => Number(n) || Infinity)),
    }

    if (seatAssignments.length === 1) {
      consolidated.section = seatAssignments[0].section
      consolidated.row = seatAssignments[0].row
      consolidated.seats = seatAssignments[0].seats
    } else {
      delete consolidated.section
      delete consolidated.row
      delete consolidated.seats
    }

    return consolidated
  })
}

export function formatBulkRowSeatSummary(row) {
  return getBulkRowSeatAssignments(row)
    .map((assignment) => {
      const section = String(assignment.section ?? '').trim()
      const rowLabel = String(assignment.row ?? '').trim()
      const seats = String(assignment.seats ?? '').trim()
      return `${section}/${rowLabel}: ${seats}`
    })
    .join('; ')
}

/** Paciolan / season-renewal export headers (SECT, SEAT#S, SEAT1S, …). */
export function normalizeSeasonExportHeader(raw) {
  return String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/#/g, '')
    .replace(/\s+/g, '')
}

function buildSeasonExportColumnIndex(headerRow) {
  const byNorm = {}
  if (!Array.isArray(headerRow)) return byNorm
  headerRow.forEach((cell, colIndex) => {
    const norm = normalizeSeasonExportHeader(cell)
    if (!norm) return
    if (!byNorm[norm]) byNorm[norm] = []
    byNorm[norm].push(colIndex)
  })
  return byNorm
}

export function detectSeasonExportFormat(headerRow) {
  if (!Array.isArray(headerRow) || !headerRow.length) return null
  const cols = buildSeasonExportColumnIndex(headerRow)

  if (cols.sect1?.length || cols.seat1s?.length) {
    return 'multi-block'
  }

  const hasSect = Boolean(cols.sect?.length)
  const hasRow = Boolean(cols.row?.length)
  const seatCols = cols.seat || []
  const hasSeatRange =
    Boolean(cols.seate?.length) ||
    seatCols.length >= 2 ||
    Boolean(cols.seats?.length && cols.seate?.length)

  if (hasSect && hasRow && (seatCols.length >= 1 || cols.seats?.length)) {
    return hasSeatRange ? 'single-block-range' : 'single-block'
  }

  return null
}

function normalizeCompleteInfoHeader(raw) {
  return String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/[#\s_-]+/g, '')
}

function buildCompleteInfoColumnIndex(headerRow) {
  const byNorm = {}
  if (!Array.isArray(headerRow)) return byNorm
  headerRow.forEach((cell, colIndex) => {
    const norm = normalizeCompleteInfoHeader(cell)
    if (!norm) return
    if (!byNorm[norm]) byNorm[norm] = []
    byNorm[norm].push(colIndex)
  })
  return byNorm
}

/** Paciolan complete-info list: section_name, row_name, seat_num, last_seat, name, email_addr. */
export function detectCompleteInfoListFormat(headerRow) {
  if (!Array.isArray(headerRow) || !headerRow.length) return null
  const cols = buildCompleteInfoColumnIndex(headerRow)
  const hasSection = Boolean(cols.sectionname?.length)
  const hasSeats = Boolean(
    cols.seatnum?.length || cols.lastseat?.length || cols.numseats?.length
  )
  const hasContact = Boolean(cols.emailaddr?.length || cols.name?.length)
  if (hasSection && hasSeats && hasContact) return 'complete-info-list'
  return null
}

function parseCompleteInfoListDataRows(grid, headerRowIndex = 0) {
  const headerRow = grid[0]
  const cols = buildCompleteInfoColumnIndex(headerRow)
  const excelRowOffset = headerRowIndex + 1
  const rows = []

  for (let r = 1; r < grid.length; r += 1) {
    const line = grid[r]
    if (isSpreadsheetRowEmpty(line)) continue

    const section = String(
      seasonExportCellValue(line, cols.sectionname?.[0])
    ).trim()
    if (!section) continue

    const rowLabel = seasonExportCellValue(line, cols.rowname?.[0])
    const seats = expandSeatRange(
      seasonExportCellValue(line, cols.seatnum?.[0]),
      seasonExportCellValue(line, cols.lastseat?.[0] ?? cols.seatnum?.[0])
    )
    const nameRaw = String(seasonExportCellValue(line, cols.name?.[0])).trim()
    const { first_name, last_name } = parseSeasonExportName(nameRaw, '')
    const email = String(
      seasonExportCellValue(line, cols.emailaddr?.[0])
    ).trim()
    const phone = firstNonEmptySeasonExportPhone(line, cols.phoneday || [])
    const priceCode = String(
      seasonExportCellValue(line, cols.pricecode?.[0])
    ).trim()
    const priceCodeDesc = String(
      seasonExportCellValue(line, cols.pricecodedesc?.[0])
    ).trim()
    const price = seasonExportCellValue(
      line,
      cols.blockpurchaseprice?.[0] ?? cols.boughtamount?.[0]
    )
    const numSeats = seasonExportCellValue(line, cols.numseats?.[0])

    const row = {
      excelRow: excelRowOffset + r,
      first_name,
      last_name,
      email,
      section,
      row: String(rowLabel ?? '').trim(),
      seats: seats.join(','),
      price_tier: section,
    }

    if (phone) row.phone_number = phone
    if (priceCode || priceCodeDesc) {
      row.buyer_type_code = priceCode || priceCodeDesc
    }
    if (price != null && String(price).trim() !== '') {
      row.price = price
    }
    if (numSeats != null && String(numSeats).trim() !== '') {
      row.quantity = numSeats
    }

    rows.push(row)
  }

  return rows
}

function uniqueSortedSeats(seatList) {
  const uniq = [
    ...new Set(seatList.map((s) => String(s).trim()).filter(Boolean)),
  ]
  const allNumeric = uniq.every((s) => /^\d+$/.test(s))
  if (allNumeric) {
    uniq.sort((a, b) => Number(a) - Number(b))
  }
  return uniq
}

export function parseSeasonExportName(nameRaw, nameLastRaw) {
  const name = String(nameRaw ?? '').trim()
  const nameLast = String(nameLastRaw ?? '').trim()

  if (nameLast.includes(',')) {
    const [lastPart, ...firstParts] = nameLast.split(',').map((s) => s.trim())
    const first = firstParts.join(' ').trim()
    if (first && lastPart) {
      return { first_name: first, last_name: lastPart }
    }
  }

  if (name.includes(',')) {
    const [lastPart, ...firstParts] = name.split(',').map((s) => s.trim())
    const first = firstParts.join(' ').trim()
    if (first && lastPart) {
      return { first_name: first, last_name: lastPart }
    }
  }

  if (name) {
    const parts = name.split(/\s+/).filter(Boolean)
    if (parts.length >= 2) {
      return { first_name: parts[0], last_name: parts.slice(1).join(' ') }
    }
    return { first_name: name, last_name: name }
  }

  if (nameLast) {
    if (nameLast.includes(',')) {
      const [lastPart, ...firstParts] = nameLast.split(',').map((s) => s.trim())
      return {
        first_name: firstParts.join(' ') || lastPart,
        last_name: lastPart || firstParts.join(' '),
      }
    }
    return { first_name: nameLast, last_name: nameLast }
  }

  return { first_name: '', last_name: '' }
}

function seasonExportCellValue(rowArray, colIndex) {
  if (colIndex == null || colIndex < 0) return ''
  const value = rowArray[colIndex]
  if (value == null) return ''
  return value
}

function isSpreadsheetRowEmpty(rowArray) {
  return !rowArray || !rowArray.some((c) => String(c ?? '').trim() !== '')
}

function firstNonEmptySeasonExportPhone(rowArray, phoneColIndices) {
  if (!phoneColIndices?.length) return ''
  for (const idx of phoneColIndices) {
    const value = String(seasonExportCellValue(rowArray, idx)).trim()
    if (value) return value
  }
  return ''
}

function extractSeasonExportContactFields(rowArray, contactColIndex) {
  const name = String(
    seasonExportCellValue(rowArray, contactColIndex.name)
  ).trim()
  const nameLast = String(
    seasonExportCellValue(rowArray, contactColIndex.nameLast)
  ).trim()
  const { first_name, last_name } = parseSeasonExportName(name, nameLast)

  const contact = {
    first_name,
    last_name,
    email: String(
      seasonExportCellValue(rowArray, contactColIndex.email)
    ).trim(),
    phone_number: firstNonEmptySeasonExportPhone(
      rowArray,
      contactColIndex.phones
    ),
    buyer_type_code: String(
      seasonExportCellValue(rowArray, contactColIndex.code)
    ).trim(),
  }

  const customFeeRaw = seasonExportCellValue(
    rowArray,
    contactColIndex.custom_fee
  )
  if (customFeeRaw != null && String(customFeeRaw).trim() !== '') {
    contact.custom_fee = String(customFeeRaw).trim()
  }

  const priceRaw = seasonExportCellValue(rowArray, contactColIndex.price)
  if (priceRaw != null && String(priceRaw).trim() !== '') {
    contact.price = priceRaw
  }

  if (name && nameLast && name.toLowerCase() === nameLast.toLowerCase()) {
    contact.organization = name
  }

  return contact
}

function buildSeasonExportSeatBlocksFromMultiBlockRow(rowArray, headerRow) {
  const cols = buildSeasonExportColumnIndex(headerRow)
  const blocks = []

  for (let n = 1; n <= 5; n += 1) {
    const sectCol = cols[`sect${n}`]?.[0]
    const rowCol = cols[`row${n}`]?.[0]
    const seatStartCol = cols[`seat${n}s`]?.[0]
    const seatEndCol = cols[`seat${n}e`]?.[0]
    if (sectCol == null) continue

    const section = seasonExportCellValue(rowArray, sectCol)
    if (!String(section).trim()) continue

    const rowLabel = seasonExportCellValue(rowArray, rowCol)
    const seats = expandSeatRange(
      seasonExportCellValue(rowArray, seatStartCol),
      seasonExportCellValue(rowArray, seatEndCol)
    )
    if (!seats.length) continue

    const totalCol = cols[`total${n}`]?.[0]
    const pricePerCol =
      cols.priceper?.[n - 1] ??
      cols.priceper?.[0] ??
      cols.priceperseat?.[n - 1] ??
      cols.priceperseat?.[0] ??
      cols.price?.[n - 1]
    const priceTierCol = cols.pricetier?.[n - 1] ?? cols.pricetier?.[0]
    blocks.push({
      section,
      row: rowLabel,
      seats,
      price: totalCol != null ? seasonExportCellValue(rowArray, totalCol) : '',
      price_per:
        pricePerCol != null ? seasonExportCellValue(rowArray, pricePerCol) : '',
      price_tier:
        priceTierCol != null
          ? seasonExportCellValue(rowArray, priceTierCol)
          : '',
    })
  }

  return blocks
}

function mergeSeasonExportBlocksBySectionRow(blocks) {
  const merged = new Map()

  for (const block of blocks) {
    const section = String(block.section ?? '').trim()
    const rowLabel = String(block.row ?? '').trim()
    const key = `${section.toLowerCase()}|${rowLabel.toLowerCase()}`

    if (!merged.has(key)) {
      merged.set(key, {
        section,
        row: rowLabel,
        seats: [...block.seats],
        price: block.price,
        price_per: block.price_per,
        price_tier: block.price_tier,
      })
      continue
    }

    const existing = merged.get(key)
    existing.seats.push(...block.seats)
    existing.price_per = preferNonEmpty(existing.price_per, block.price_per)
    existing.price_tier = preferNonEmpty(existing.price_tier, block.price_tier)
    const existingPrice = Number(String(existing.price ?? '').trim())
    const blockPrice = Number(String(block.price ?? '').trim())
    if (Number.isFinite(existingPrice) && Number.isFinite(blockPrice)) {
      existing.price = existingPrice + blockPrice
    }
  }

  return [...merged.values()].map((block) => ({
    ...block,
    seats: uniqueSortedSeats(block.seats),
  }))
}

function parseSeasonExportDataRows(grid, format, headerRowIndex = 0) {
  const headerRow = grid[0]
  const cols = buildSeasonExportColumnIndex(headerRow)
  const contactColIndex = {
    name: cols.name?.[0],
    nameLast: cols.namelast?.[0],
    email: cols.email?.[0],
    phones: cols.phone || [],
    code: cols.code?.[0],
    price: cols.subtot?.[0],
    custom_fee: cols.customfee?.[0] ?? cols.custom_fee?.[0],
  }

  const rows = []
  const excelRowOffset = headerRowIndex + 1

  for (let r = 1; r < grid.length; r += 1) {
    const line = grid[r]
    if (isSpreadsheetRowEmpty(line)) continue

    const excelRow = excelRowOffset + r
    const contact = extractSeasonExportContactFields(line, contactColIndex)
    let blocks = []

    if (format === 'multi-block') {
      blocks = mergeSeasonExportBlocksBySectionRow(
        buildSeasonExportSeatBlocksFromMultiBlockRow(line, headerRow)
      )
    } else {
      const section = seasonExportCellValue(line, cols.sect?.[0])
      const rowLabel = seasonExportCellValue(line, cols.row?.[0])
      const seatStartCol = cols.seats?.[0] ?? cols.seat?.[0]
      const seatEndCol = cols.seate?.[0] ?? cols.seat?.[1]
      const seats = expandSeatRange(
        seasonExportCellValue(line, seatStartCol),
        seasonExportCellValue(line, seatEndCol)
      )

      if (String(section).trim() && seats.length) {
        blocks = [
          {
            section,
            row: rowLabel,
            seats,
            price: seasonExportCellValue(line, cols.subtot?.[0]),
            price_per: seasonExportCellValue(
              line,
              cols.priceper?.[0] ?? cols.priceperseat?.[0]
            ),
            price_tier: seasonExportCellValue(line, cols.pricetier?.[0]),
          },
        ]
      }
    }

    for (const block of blocks) {
      const row = {
        excelRow,
        ...contact,
        section: block.section,
        row: block.row,
        seats: block.seats.join(','),
      }

      if (block.price != null && String(block.price).trim() !== '') {
        row.price = block.price
      } else if (contact.price != null && String(contact.price).trim() !== '') {
        row.price = contact.price
      }
      if (block.price_per != null && String(block.price_per).trim() !== '') {
        row.price_per = block.price_per
      }
      if (block.price_tier != null && String(block.price_tier).trim() !== '') {
        row.price_tier = block.price_tier
      }

      rows.push(row)
    }
  }

  return rows
}

const MAX_HEADER_SCAN_ROWS = 15

/** Some Paciolan / Excel exports prepend title rows (e.g. "Table 1") above the real header. */
function findSupportedHeaderRow(grid) {
  if (!Array.isArray(grid) || !grid.length) return null

  const limit = Math.min(grid.length, MAX_HEADER_SCAN_ROWS)
  for (let i = 0; i < limit; i += 1) {
    const row = grid[i]
    if (isSpreadsheetRowEmpty(row)) continue

    const seasonFormat = detectSeasonExportFormat(row)
    if (seasonFormat) {
      return { index: i, kind: 'season-export', format: seasonFormat }
    }

    const completeInfoFormat = detectCompleteInfoListFormat(row)
    if (completeInfoFormat) {
      return {
        index: i,
        kind: 'complete-info-list',
        format: completeInfoFormat,
      }
    }

    const headerMap = buildHeaderMap(row)
    const found = new Set(Object.values(headerMap))
    const missing = REQUIRED_CANONICAL.filter((k) => !found.has(k))
    if (!missing.length) {
      return { index: i, kind: 'standard' }
    }
  }

  return null
}

function pickWorkbookGrid(workbook, XLSX) {
  let best = null

  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName]
    const grid = XLSX.utils.sheet_to_json(sheet, {
      header: 1,
      defval: '',
      raw: false,
    })
    if (!grid.length) continue

    const headerMatch = findSupportedHeaderRow(grid)
    if (!headerMatch) continue

    const gridFromHeader = grid.slice(headerMatch.index)
    const excelRowOffset = headerMatch.index + 1

    if (headerMatch.kind === 'season-export') {
      const rows = parseSeasonExportDataRows(
        gridFromHeader,
        headerMatch.format,
        headerMatch.index
      )
      if (rows.length && (!best || rows.length > best.rows.length)) {
        best = { rows, format: 'season-export', sheetName }
      }
      continue
    }

    if (headerMatch.kind === 'complete-info-list') {
      const rows = parseCompleteInfoListDataRows(
        gridFromHeader,
        headerMatch.index
      )
      if (rows.length && (!best || rows.length > best.rows.length)) {
        best = { rows, format: 'complete-info-list', sheetName }
      }
      continue
    }

    const headerMap = buildHeaderMap(gridFromHeader[0])
    const rows = []
    for (let r = 1; r < gridFromHeader.length; r += 1) {
      const line = gridFromHeader[r]
      if (isSpreadsheetRowEmpty(line)) continue
      rows.push(rowArrayToObject(headerMap, line, excelRowOffset + r))
    }
    if (rows.length && (!best || rows.length > best.rows.length)) {
      best = { rows, format: 'standard', sheetName }
    }
  }

  return best
}

/**
 * Clean a cell value for phone parsing (Excel text prefix, tel: URL, unicode dashes).
 * @param {unknown} raw
 * @returns {string}
 */
function preprocessPhoneCell(raw) {
  if (raw == null) return ''
  let s =
    typeof raw === 'number' && Number.isFinite(raw)
      ? String(Math.trunc(raw))
      : String(raw)
  s = s.trim()
  if (!s) return ''
  if (s.startsWith("'")) s = s.slice(1).trim()
  s = s.replace(/^tel:/i, '').trim()
  s = s.replace(/[\u2010-\u2015\u2212\u00AD]/g, '-')
  s = s.replace(/\s+/g, ' ')
  return s
}

/**
 * Parse a phone from a spreadsheet cell to E.164 (matches admin PhoneInput / isValidPhoneNumber).
 * @param {unknown} raw
 * @returns {string} E.164 if valid, "" if the cell is empty
 * @returns {null} if non-empty but not a valid number
 */
export function normalizeBulkReservedPhoneNumber(raw) {
  const s = preprocessPhoneCell(raw)
  if (!s) return ''
  const withCountry = parsePhoneNumber(s, DEFAULT_BULK_PHONE_COUNTRY)
  if (withCountry?.isValid()) {
    return withCountry.format('E.164')
  }
  const intl = s.startsWith('+') ? parsePhoneNumber(s) : undefined
  if (intl?.isValid()) {
    return intl.format('E.164')
  }
  return null
}

export function validateDataRow(row, options = {}) {
  const { organizationUUID } = options
  const errs = []
  const email = String(row.email ?? '').trim()
  const first = String(row.first_name ?? '').trim()
  const last = String(row.last_name ?? '').trim()
  const isWholeRowGa =
    parseTruthyGaCell(row.ga) ||
    rowLabelIndicatesGa(row.row) ||
    sectionLabelIndicatesGa(row.section)
  const assignments = getBulkRowSeatAssignments(row)

  if (!email) errs.push('email is required')
  if (!first) errs.push('first_name is required')
  if (!last) errs.push('last_name is required')

  if (isWholeRowGa) {
    const assignment = assignments[0] || {}
    const section = assignment.section
    const seatsRaw = assignment.seats
    if (section == null || String(section).trim() === '')
      errs.push('section is required')

    const qRaw = row.quantity
    const hasQuantityCol =
      qRaw != null &&
      String(qRaw).trim() !== '' &&
      Number.isFinite(Number(qRaw))
    const seatsStr = String(seatsRaw ?? '').trim()
    const numericSeats = /^\d+$/.test(seatsStr)
    const seatTokens = parseSeatTokens(seatsRaw)
    if (!hasQuantityCol && !numericSeats && seatTokens.length === 0) {
      errs.push(
        'GA rows need quantity (column) or a seat count (e.g. seats=4 or comma-separated placeholders)'
      )
    }
    if (hasQuantityCol && Number(qRaw) < 1) {
      errs.push('quantity must be at least 1 for GA rows')
    }
  } else {
    for (const assignment of assignments) {
      const section = assignment.section
      const rowLabel = assignment.row
      const seatsRaw = assignment.seats
      const assignmentIsGa = isBulkReservedGaAssignment(
        assignment,
        organizationUUID,
        row
      )
      const label =
        assignments.length > 1
          ? `section ${String(section ?? '').trim() || '?'} row ${String(rowLabel ?? '').trim() || '?'}`
          : null

      if (assignmentIsGa) {
        if (section == null || String(section).trim() === '') {
          errs.push(
            label ? `${label}: section is required` : 'section is required'
          )
        }

        const qRaw = row.quantity
        const hasQuantityCol =
          assignments.length === 1 &&
          qRaw != null &&
          String(qRaw).trim() !== '' &&
          Number.isFinite(Number(qRaw))
        const seatsStr = String(seatsRaw ?? '').trim()
        const numericSeats = /^\d+$/.test(seatsStr)
        const seatTokens = parseSeatTokens(seatsRaw)
        if (!hasQuantityCol && !numericSeats && seatTokens.length === 0) {
          errs.push(
            label
              ? `${label}: GA rows need quantity or a seat count`
              : 'GA rows need quantity (column) or a seat count (e.g. seats=4 or comma-separated placeholders)'
          )
        }
        if (hasQuantityCol && Number(qRaw) < 1) {
          errs.push(
            label
              ? `${label}: quantity must be at least 1 for GA rows`
              : 'quantity must be at least 1 for GA rows'
          )
        }
        continue
      }

      if (section == null || String(section).trim() === '') {
        errs.push(
          label ? `${label}: section is required` : 'section is required'
        )
      }
      if (rowLabel == null || String(rowLabel).trim() === '') {
        errs.push(label ? `${label}: row is required` : 'row is required')
      }
      if (seatsRaw == null || String(seatsRaw).trim() === '') {
        errs.push(label ? `${label}: seats is required` : 'seats is required')
        continue
      }

      const seatTokens = parseSeatTokens(seatsRaw)
      if (seatTokens.length === 0) {
        errs.push(
          label
            ? `${label}: seats must list at least one seat`
            : 'seats must list at least one seat'
        )
        continue
      }
      const uniq = new Set(seatTokens)
      if (uniq.size !== seatTokens.length) {
        errs.push(
          label
            ? `${label}: duplicate seat numbers in seats column`
            : 'duplicate seat numbers in seats column'
        )
      }
    }
  }

  if (row.phone_number != null && String(row.phone_number).trim() !== '') {
    if (normalizeBulkReservedPhoneNumber(row.phone_number) === null) {
      errs.push(
        'phone_numbers is not a valid phone number (use a full number, +country code, or set REACT_APP_DEFAULT_PHONE_COUNTRY for your region)'
      )
    }
  }

  if (row.price_tier_unmatched) {
    errs.push(
      `PRICE TIER "${row.price_tier_unmatched}" does not match any package pricing tier`
    )
  }

  if (row.buyer_type_code_unmatched) {
    errs.push(
      `CODE "${row.buyer_type_code_unmatched}" does not match any buyer type`
    )
  }

  if (row.custom_fee_unmatched) {
    errs.push(
      `CUSTOM FEE "${row.custom_fee_unmatched}" does not match any custom fee`
    )
  }

  return errs
}

export const MISSING_EMAIL_SKIP_REASON = 'Skipped: email is required'

export function isRowMissingEmail(row) {
  return !String(row.email ?? '').trim()
}

/** Validation errors that block upload (missing email is handled separately). */
export function validateBlockingDataRow(row, options = {}) {
  return validateDataRow(row, options).filter(
    (message) => message !== 'email is required'
  )
}

export function partitionRowsForBulkUpload(rows) {
  const uploadRows = []
  const skippedRows = []

  for (const row of rows) {
    if (isRowMissingEmail(row)) {
      skippedRows.push({
        excelRow: row.excelRow,
        ok: false,
        error: MISSING_EMAIL_SKIP_REASON,
        skipped: true,
      })
      continue
    }
    uploadRows.push(row)
  }

  return { uploadRows, skippedRows }
}

export function mergeBulkImportResults(serverResults, skippedRows) {
  const server = Array.isArray(serverResults) ? serverResults : []
  const skipped = Array.isArray(skippedRows) ? skippedRows : []
  if (!skipped.length) return server
  return [...server, ...skipped]
}

/**
 * @param {import("xlsx").WorkBook} workbook
 * @param {typeof import("xlsx")} XLSX
 */
export function parseWorkbookToRows(workbook, XLSX) {
  if (!workbook.SheetNames?.length) {
    return { error: 'The file has no sheets.' }
  }

  const picked = pickWorkbookGrid(workbook, XLSX)
  if (!picked) {
    return {
      error: `No supported sheet found. Use standard columns (${REQUIRED_CANONICAL.join(', ')}), a season renewal export with SECT/ROW and SEAT#S/SEAT#E (or SECT1/ROW1/SEAT1S/SEAT1E) plus NAME, EMAIL, and related contact columns, or a complete info list with section_name, row_name, seat_num/last_seat, name, and email_addr.`,
    }
  }

  const { rows, sheetName, format } = picked

  if (!rows.length) {
    return { error: 'No data rows found below the header.' }
  }

  if (rows.length > MAX_BULK_RESERVED_ROWS) {
    return {
      error: `At most ${MAX_BULK_RESERVED_ROWS} data rows allowed per upload (sheet "${sheetName}" expands to ${rows.length}).`,
    }
  }

  return {
    rows,
    sheetName,
    format,
  }
}

export function rowsToBulkApiPayload(rows, options = {}) {
  const { organizationUUID } = options
  return rows.map((r) => {
    const priceRaw = r.price
    const priceNum = parseBulkImportAmount(priceRaw)
    const normalizedPhone =
      r.phone_number != null && String(r.phone_number).trim() !== ''
        ? normalizeBulkReservedPhoneNumber(r.phone_number)
        : ''
    const seatAssignments = getBulkRowSeatAssignments(r)

    const out = {
      excelRow: r.excelRow,
      organization: r.organization,
      email: String(r.email ?? '').trim(),
      first_name: String(r.first_name ?? '').trim(),
      last_name: String(r.last_name ?? '').trim(),
    }

    if (r.buyer_type_code != null && String(r.buyer_type_code).trim() !== '') {
      out.buyer_type_code = String(r.buyer_type_code).trim()
    }
    if (
      r.selectedBuyerType != null &&
      String(r.selectedBuyerType).trim() !== ''
    ) {
      out.selectedBuyerType = r.selectedBuyerType
    }

    if (seatAssignments.length === 1) {
      out.section = seatAssignments[0].section
      out.row = seatAssignments[0].row
      out.seats = seatAssignments[0].seats
    } else {
      out.seat_assignments = seatAssignments.map((assignment) => ({
        section: assignment.section,
        row: assignment.row,
        seats: assignment.seats,
        ...(isBulkReservedGaAssignment(assignment, organizationUUID, r)
          ? { ga: true }
          : {}),
      }))
    }

    if (normalizedPhone) {
      out.phone_number = normalizedPhone
    }
    const allAssignmentsGa =
      seatAssignments.length > 0 &&
      seatAssignments.every((assignment) =>
        isBulkReservedGaAssignment(assignment, organizationUUID, r)
      )
    if (
      allAssignmentsGa ||
      ((parseTruthyGaCell(r.ga) ||
        rowLabelIndicatesGa(r.row) ||
        sectionLabelIndicatesGa(r.section)) &&
        seatAssignments.length <= 1)
    ) {
      out.ga = true
    }
    if (r.quantity != null && String(r.quantity).trim() !== '') {
      out.quantity = r.quantity
    }
    if (priceRaw != null && String(priceRaw).trim() !== '') {
      out.price = priceNum != null ? priceNum : priceRaw
    }
    if (r.price_tier != null && String(r.price_tier).trim() !== '') {
      out.price_tier = String(r.price_tier).trim()
    }
    if (r.price_per != null && String(r.price_per).trim() !== '') {
      const pricePerNum = parseBulkImportAmount(r.price_per)
      out.price_per = pricePerNum != null ? pricePerNum : r.price_per
    }
    if (r.pricingTier && typeof r.pricingTier === 'object') {
      out.pricingTier = r.pricingTier
    }
    if (r.custom_fee != null && String(r.custom_fee).trim() !== '') {
      out.custom_fee = String(r.custom_fee).trim()
    }
    if (Array.isArray(r.customFeeIds) && r.customFeeIds.length) {
      out.customFeeIds = r.customFeeIds
    }
    if (r.totalFeeAmount != null && String(r.totalFeeAmount).trim() !== '') {
      const totalFeeNum = Number(r.totalFeeAmount)
      out.totalFeeAmount = Number.isFinite(totalFeeNum)
        ? totalFeeNum
        : r.totalFeeAmount
    }
    return out
  })
}
